import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Check, Crown, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const Subscription = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
  }, [searchParams]);

  const checkPaymentStatus = async (sessionId, attempt = 0) => {
    if (attempt >= 5) {
      toast.error('Payment verification timed out');
      return;
    }

    setCheckingPayment(true);
    try {
      const response = await api.get(`/payments/status/${sessionId}`);
      if (response.data.payment_status === 'paid') {
        const plan = response.data.plan;
        const isElite = plan.includes('elite');
        toast.success(isElite ? '🎉 Welcome to Elite!' : '🎉 Welcome to Premium!');
        
        // Refresh user data to get updated subscription tier
        window.location.href = '/dashboard';
      } else {
        setTimeout(() => checkPaymentStatus(sessionId, attempt + 1), 2000);
      }
    } catch (error) {
      toast.error('Failed to verify payment');
    } finally {
      if (attempt === 4) setCheckingPayment(false);
    }
  };

  const handleUpgrade = async (packageId) => {
    setLoading(true);
    try {
      const originUrl = window.location.origin;
      const response = await api.post('/payments/create-checkout', null, {
        params: { package_id: packageId, origin_url: originUrl }
      });
      window.location.href = response.data.url;
    } catch (error) {
      toast.error('Failed to start checkout');
      setLoading(false);
    }
  };

  const freeFeatures = [
    'Unlimited gift lists',
    'Budget tracking',
    'Gift management',
    'Event organization',
    'Search & filter'
  ];

  const premiumFeatures = [
    'Unlimited gift lists',
    'Budget tracking',
    'Gift management',
    'Event organization',
    'Search & filter',
    'Recipient profiles',
    'Gift history tracking',
    'Share lists',
    'Export to CSV/PDF'
  ];

  const eliteFeatures = [
    'Unlimited gift lists',
    'Budget tracking',
    'Gift management',
    'Event organization',
    'Search & filter',
    'Recipient profiles',
    'Gift history tracking',
    'Received gifts tracking',
    'Email reminders',
    'Share lists',
    'Export to CSV/PDF',
    'Spending reports',
    'Priority support'
  ];

  if (checkingPayment) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-red-50 to-green-50 flex items-center justify-center">
        <Card className="glass-card max-w-md">
          <CardContent className="p-8 text-center">
            <div className="animate-spin w-12 h-12 border-4 border-red-600 border-t-transparent rounded-full mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">Processing payment...</h3>
            <p className="text-gray-600">Please wait while we verify your payment</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-red-50 to-green-50" data-testid="subscription-page">
      <header className="bg-white shadow-sm border-b border-red-100">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <Button variant="ghost" onClick={() => navigate('/dashboard')} data-testid="back-button">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold mb-4">
            <span className="gradient-text">🎄 Upgrade to Premium 🎄</span>
          </h1>
          <p className="text-xl text-gray-600">Unlock powerful features to make gift-giving effortless</p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 max-w-7xl mx-auto">
          {/* Free Tier */}
          <Card className="glass-card relative">
            <CardHeader>
              <CardTitle className="text-center">
                <div className="text-2xl font-bold mb-2">Free</div>
                <div className="text-4xl font-bold text-red-600 mb-2">$0</div>
                <div className="text-sm text-gray-600">forever</div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 mb-6">
                {freeFeatures.map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-sm">
                    <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              {user?.subscription_tier === 'free' ? (
                <Button className="w-full" disabled data-testid="current-plan-button">
                  Current Plan
                </Button>
              ) : (
                <Button className="w-full" variant="outline" disabled>
                  Free Plan
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Premium Tier */}
          <Card className="glass-card relative border-2 border-red-400 shadow-lg">
            <div className="absolute top-0 right-0 bg-red-600 text-white px-3 py-1 rounded-bl-lg font-semibold text-sm">
              POPULAR
            </div>
            <CardHeader>
              <CardTitle className="text-center">
                <div className="flex items-center justify-center gap-2 text-2xl font-bold mb-2">
                  <Crown className="w-7 h-7 text-red-600" />
                  Premium
                </div>
                <div className="text-4xl font-bold text-red-600 mb-2">$19.99</div>
                <div className="text-sm text-gray-600">per year</div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 mb-6">
                {premiumFeatures.map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-sm">
                    <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                    <span className={idx >= freeFeatures.length ? 'font-semibold' : ''}>{feature}</span>
                  </li>
                ))}
              </ul>
              {user?.subscription_tier === 'premium' ? (
                <Button className="w-full bg-red-600 hover:bg-red-700" disabled data-testid="active-premium-button">
                  <Crown className="w-4 h-4 mr-2" />
                  Active
                </Button>
              ) : (
                <>
                  <Button
                    className="w-full bg-red-600 hover:bg-red-700"
                    onClick={() => handleUpgrade('premium_annual')}
                    disabled={loading}
                    data-testid="upgrade-premium-annual-button"
                  >
                    {loading ? 'Processing...' : '🎄 Upgrade to Premium'}
                  </Button>
                  <Button
                    className="w-full mt-2"
                    variant="outline"
                    onClick={() => handleUpgrade('premium_monthly')}
                    disabled={loading}
                    data-testid="upgrade-premium-monthly-button"
                  >
                    Or $1.99/month
                  </Button>
                </>
              )}
            </CardContent>
          </Card>

          {/* Elite Tier */}
          <Card className="glass-card relative border-2 border-amber-400 shadow-xl">
            <div className="absolute top-0 right-0 bg-gradient-to-r from-amber-400 to-amber-600 text-white px-3 py-1 rounded-bl-lg font-semibold text-sm">
              ⭐ BEST VALUE
            </div>
            <CardHeader>
              <CardTitle className="text-center">
                <div className="flex items-center justify-center gap-2 text-2xl font-bold mb-2">
                  <Crown className="w-7 h-7 text-amber-500" />
                  Elite
                </div>
                <div className="text-4xl font-bold text-amber-600 mb-2">$39.99</div>
                <div className="text-sm text-gray-600 mb-1">per year</div>
                <div className="text-xs text-green-600 font-semibold">Everything included!</div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 mb-6">
                {eliteFeatures.map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-sm">
                    <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                    <span className={idx >= premiumFeatures.length ? 'font-semibold text-amber-700' : ''}>{feature}</span>
                  </li>
                ))}
              </ul>
              {user?.subscription_tier === 'elite' ? (
                <Button className="w-full bg-amber-500 hover:bg-amber-600" disabled data-testid="active-elite-button">
                  <Crown className="w-4 h-4 mr-2" />
                  Active Elite
                </Button>
              ) : (
                <>
                  <Button
                    className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white"
                    onClick={() => handleUpgrade('elite_annual')}
                    disabled={loading}
                    data-testid="upgrade-elite-annual-button"
                  >
                    {loading ? 'Processing...' : '⭐ Upgrade to Elite'}
                  </Button>
                  <Button
                    className="w-full mt-2"
                    variant="outline"
                    onClick={() => handleUpgrade('elite_monthly')}
                    disabled={loading}
                    data-testid="upgrade-elite-monthly-button"
                  >
                    Or $3.99/month
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="text-center mt-8">
          <p className="text-gray-600">Payment methods accepted:</p>
          <div className="flex justify-center gap-4 mt-2">
            <span className="px-4 py-2 bg-white rounded-lg shadow">💳 Credit Card</span>
            <span className="px-4 py-2 bg-white rounded-lg shadow">Google Pay</span>
            <span className="px-4 py-2 bg-white rounded-lg shadow">Apple Pay</span>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Subscription;
